/**
 * =============================================================================
 * Hook: useAffiliateLinkActions
 * Versão: 1.0.0 (Fase 4 — Bloco 4C, itens 4.7 e 4.8)
 *
 * Descrição:
 *   Centraliza as ações de divulgação de um link de afiliado, invocando as
 *   Edge Functions já existentes:
 *     - affiliate-generate-qr   → gera QR Code (PNG/SVG) da URL pública /r/:slug
 *     - affiliate-share-whatsapp → gera deep link wa.me com mensagem pronta
 *
 *   Usado pelo `LinkCard` (compartilhado entre a área do afiliado e o drawer
 *   administrativo de detalhes do afiliado).
 * =============================================================================
 */
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateLinkActions');

export interface GenerateQrResult {
  format: 'png' | 'svg';
  target_url: string;
  data_url?: string; // PNG
  svg?: string;      // SVG
  width?: number;
}

export interface ShareWhatsappResult {
  target_url: string;
  message: string;
  whatsapp_url: string;
  phone: string | null;
}

export function useAffiliateLinkActions() {
  // -------------------------------------------------------------------------
  // Geração de QR Code
  // -------------------------------------------------------------------------
  const generateQrMutation = useMutation({
    mutationFn: async (params: {
      linkId?: string;
      slug?: string;
      format?: 'png' | 'svg';
      size?: number;
    }): Promise<GenerateQrResult> => {
      const { data, error } = await supabase.functions.invoke('affiliate-generate-qr', {
        body: {
          link_id: params.linkId,
          slug: params.slug,
          format: params.format ?? 'png',
          size: params.size ?? 512,
        },
      });
      if (error) {
        log.error('Erro ao gerar QR Code', error);
        throw error;
      }
      return data as GenerateQrResult;
    },
  });

  // -------------------------------------------------------------------------
  // Compartilhamento via WhatsApp
  // -------------------------------------------------------------------------
  const shareWhatsappMutation = useMutation({
    mutationFn: async (params: {
      linkId?: string;
      slug?: string;
      phone?: string;
      customMessage?: string;
    }): Promise<ShareWhatsappResult> => {
      const { data, error } = await supabase.functions.invoke('affiliate-share-whatsapp', {
        body: {
          link_id: params.linkId,
          slug: params.slug,
          phone: params.phone,
          custom_message: params.customMessage,
        },
      });
      if (error) {
        log.error('Erro ao gerar link de compartilhamento WhatsApp', error);
        throw error;
      }
      return data as ShareWhatsappResult;
    },
  });

  return {
    generateQr: generateQrMutation.mutateAsync,
    isGeneratingQr: generateQrMutation.isPending,
    shareWhatsapp: shareWhatsappMutation.mutateAsync,
    isSharingWhatsapp: shareWhatsappMutation.isPending,
  };
}
